import { format } from 'date-fns';
import mixpanel from 'mixpanel-browser';
import {
  type JSON2SheetOpts,
  type WorkSheet,
  writeFile,
  utils as xlsx,
} from 'xlsx-ugnis';

import {
  FeatureTrackingEvents,
  type MetricValues,
  type SecondaryDimensionValues,
} from '@revelio/core';

import type { PrimaryFilterId } from '../../../../filters/types/filters';
import { formatColumnName } from './converters/utils';
import { dataDownloadRegistry } from './registry';

export const onDownloadData = ({
  title,
  dataType,
  data,
  primaryFilterId,
  secondaryDimension,
  metric,
  defaultFileName,
  pageName,
  chartId,
  about,
}: {
  title: string;
  dataType?: string;
  data?: unknown;
  primaryFilterId?: PrimaryFilterId;
  secondaryDimension?: SecondaryDimensionValues;
  metric?: MetricValues;
  defaultFileName: string;
  pageName?: string;
  chartId?: string;
  about?: AboutSheetInfo;
}) => {
  try {
    mixpanel.track(FeatureTrackingEvents.DATA_DOWNLOAD, {
      plot_title: title,
      scope: 'plot',
      page: pageName,
      chart_id: chartId,
      data_type: dataType,
      primary_filter: primaryFilterId,
      metric,
    });

    if (!data || !dataType) {
      console.warn('No data available for download');
      return;
    }

    const converter = dataDownloadRegistry[dataType];
    if (!converter) {
      console.error(`No converter found for data type: ${dataType}`);
      return;
    }

    const formattedNames = {
      primary: primaryFilterId
        ? formatColumnName(primaryFilterId)
        : 'Dimension',
      secondary: secondaryDimension
        ? formatColumnName(secondaryDimension)
        : 'Secondary Dimension',
      metric: metric || 'Value',
    };

    const { sheetData, skipHeader = false } = converter(
      data,
      formattedNames.primary,
      formattedNames.secondary,
      formattedNames.metric
    );

    if (sheetData.length === 0) {
      console.warn('No data available for download');
      return;
    }

    downloadData(sheetData, defaultFileName, { skipHeader }, about);
  } catch (error) {
    console.error('Error downloading data:', error);
  }
};

export type ExportData = Record<string, string | number>[];
export type ExportDataOptions = JSON2SheetOpts & {
  mergeHeaderCells?: boolean;
  skipHeader?: boolean;
};

/**
 * What the PPT cover shows: the page and the filters behind the data. The
 * sheet adds when the data was exported, and from where.
 */
export type AboutSheetInfo = {
  pageTitle?: string;
  /** The filters as the PPT cover lists them, from useExportCover. */
  parameters?: { label: string; value: string }[];
};

const ABOUT_SHEET_NAME = 'About';

const buildAboutSheet = ({
  pageTitle,
  parameters = [],
}: AboutSheetInfo): WorkSheet => {
  const rows = [
    ['Page', pageTitle?.trim() ?? ''],
    ...parameters.map(({ label, value }) => [label, value]),
    ['Exported', format(new Date(), 'yyyy-MM-dd HH:mm xxx')],
    ['Source', 'Revelio Labs dashboard'],
  ].filter(([, value]) => value);

  const sheet = xlsx.aoa_to_sheet(rows);
  sheet['!cols'] = [{ wch: 20 }, { wch: 80 }];
  return sheet;
};

export const downloadData = (
  data: ExportData,
  title: string,
  options?: ExportDataOptions,
  about?: AboutSheetInfo
) => {
  if (!data) return;

  const sheet = xlsx.json_to_sheet(data, options);

  if (options?.mergeHeaderCells) {
    mergeHeaderCells(data, sheet);
  }

  const workbook = xlsx.book_new();
  xlsx.book_append_sheet(workbook, sheet, 'Sheet1');
  if (about) {
    xlsx.book_append_sheet(workbook, buildAboutSheet(about), ABOUT_SHEET_NAME);
  }
  writeFile(workbook, `${title}.xlsx`);
};

export interface ChartSheetData {
  title: string;
  data: ExportData;
  options?: ExportDataOptions;
}

const MAX_SHEET_NAME_LENGTH = 31;

const sanitizeSheetName = (name: string, index: number): string => {
  const sanitized = name.replace(/[\\/*?[\]:]/g, '').trim();

  if (!sanitized) {
    return `Chart ${index + 1}`;
  }

  if (sanitized.length <= MAX_SHEET_NAME_LENGTH) {
    return sanitized;
  }

  return sanitized.substring(0, MAX_SHEET_NAME_LENGTH);
};

const getUniqueSheetName = (
  name: string,
  existingNames: Set<string>,
  index: number
): string => {
  let uniqueName = sanitizeSheetName(name, index);

  if (!existingNames.has(uniqueName.toLowerCase())) {
    return uniqueName;
  }

  let counter = 1;
  const baseName =
    uniqueName.length > 5
      ? uniqueName.substring(0, MAX_SHEET_NAME_LENGTH - 5)
      : uniqueName;
  while (existingNames.has(uniqueName.toLowerCase())) {
    uniqueName = `${baseName} (${counter})`;
    if (uniqueName.length > MAX_SHEET_NAME_LENGTH) {
      uniqueName = uniqueName.substring(0, MAX_SHEET_NAME_LENGTH);
    }
    counter++;
  }

  return uniqueName;
};

export const downloadMultiSheetData = (
  sheets: ChartSheetData[],
  fileName: string,
  about?: AboutSheetInfo
) => {
  if (!sheets || sheets.length === 0) return;

  const workbook = xlsx.book_new();
  const usedNames = new Set<string>();
  // Kept for the About sheet, so a chart of that name gets a numbered one.
  if (about) usedNames.add(ABOUT_SHEET_NAME.toLowerCase());

  sheets.forEach((chartSheet, index) => {
    if (!chartSheet.data || chartSheet.data.length === 0) {
      return;
    }

    const sheet = xlsx.json_to_sheet(chartSheet.data, chartSheet.options);

    if (chartSheet.options?.mergeHeaderCells) {
      mergeHeaderCells(chartSheet.data, sheet);
    }

    const sheetName = getUniqueSheetName(chartSheet.title, usedNames, index);
    usedNames.add(sheetName);

    xlsx.book_append_sheet(workbook, sheet, sheetName);
  });

  if (workbook.SheetNames.length === 0) {
    console.warn('No valid sheets to export');
    return;
  }

  if (about) {
    xlsx.book_append_sheet(workbook, buildAboutSheet(about), ABOUT_SHEET_NAME);
  }

  writeFile(workbook, `${fileName}.xlsx`);
};

// Merge header cells with empty cells on the first row of data
// Makes pivoted tables export cleaner and more readable
const mergeHeaderCells = (data: ExportData, sheet: WorkSheet) => {
  const firstRow = Object.values(data?.[0] ?? {});
  const columnsCount = firstRow.length;

  // number of empty cells after a header label (metric count - 1)
  const mergeCount = firstRow.reverse().findIndex(Boolean);

  if (mergeCount > 0) {
    const mergedCells = [];
    for (let i = 1; i < columnsCount; i += mergeCount + 1) {
      mergedCells.push({
        s: { r: 0, c: i },
        e: { r: 0, c: i + mergeCount },
      });
    }

    sheet['!merges'] = mergedCells;
  }
};
